/**
 * The payload-aware read path (#1542): the response hook of the annotations
 * transport that turns an envelope row back into the plaintext row the store has
 * always held — or marks it unreadable, per item, with a reason the screens can
 * render. Plaintext rows (no `payload`) pass through untouched, so a member who
 * has not enrolled sees no change, and a mixed member — some rows sealed, some
 * not — sees both.
 *
 * Opening is the envelope primitive of spec section 6.7 (courses-crypto/envelope.js),
 * under the key the courses-keys store holds for the signed-in member. Both are
 * taken as dependencies so a spec can hand in a key it minted, or a decoder that
 * records what it was asked; the app installs the real ones at boot (main.js).
 *
 *   open({ envelope, memberId, type, itemId, mk, keyId }) -> Promise<fields>
 *   keyMaterial() -> { mk, keyId } | null, or a promise of one
 *
 * `keyMaterial` is awaited, and the app's version (keyMaterialFromStore) waits for
 * the key store to finish hydrating first: a row that arrives while IndexedDB is
 * still being read must not be told the device has no key, because a moment
 * later it does, and nothing re-runs the fetch to say so.
 *
 * Outcomes, per row:
 *   no payload                          -> the row, unchanged
 *   payload that is not an object       -> unreadable: FAILED
 *   a version newer than this build     -> unreadable: NEEDS_NEWER_APP
 *   no key on this device               -> unreadable: NO_KEY
 *   a running rotation's generation     -> unreadable: BEING_REENCRYPTED (#1694)
 *   open() says UNKNOWN_KEY             -> unreadable: NO_KEY (another generation:
 *                                          a rotation, or an approval still to come)
 *   the key store could not be read     -> unreadable: FAILED, named in the log
 *   open() fails otherwise              -> unreadable: FAILED, named in the log
 *   opened                              -> the row's plaintext columns filled from
 *                                          the envelope, `payload` removed
 *
 * An unreadable row leaves with its envelope removed too: the reason is all the
 * screens need, and a sealed body has no business in the store or its read cache.
 *
 * The context each hook receives — { memberId, type, itemId } — is exactly what
 * the envelope's AAD is built from (section 6.3), so a row moved between members,
 * types or ids fails to open and is shown as such, never as someone else's text.
 */
import {
  ENVELOPE_ERRORS,
  ENVELOPE_VERSION,
  open as openEnvelope,
} from '@/utils/courses-crypto/envelope';
import { UNREADABLE_REASON, UNRECOVERABLE_FIELD } from '@/constants/courses-encryption';
import { recordRevision } from './courses-encryption/annotation-revisions';
import { setAnnotationTransportHooks } from './courses-annotations-transport';

/**
 * How long a sealed row waits for the key store before it is given up on. Hydration
 * is a local IndexedDB read (plus a platform-copy lookup on a reinstall), so this is
 * generous; a store that takes longer than this has failed in some way the member
 * needs to hear about, and the row is marked failed rather than locked.
 */
export const KEY_STORE_READY_TIMEOUT_MS = 8000;

// The API sends `payload: null` on a plaintext row, so both absences count.
const isSealed = row => row?.payload !== undefined && row?.payload !== null;
const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Marks a row that arrived as an envelope. `payload` is stripped on the way
 * through, so without this nothing downstream can tell an opened row from one
 * that was never sealed — the two are identical field for field, by design.
 *
 * The plaintext migration (#1545) is the caller that needs to: a row it cannot
 * tell apart is a row it re-writes for ever, and a collect pass that never
 * comes back empty never posts `/migrated`.
 *
 * NON-ENUMERABLE on purpose. This is a fact about how the row ARRIVED, not a
 * field of the row, and it must never travel back out: object spread, JSON and
 * `toEqual` all skip it, so a write path that re-states a row's fields cannot
 * put it on the wire, where an unknown key would be rejected by the DTO.
 * Read it through `cameFromEnvelope`, never by hand.
 */
const FROM_ENVELOPE = 'fromEnvelope';

// `configurable` because some of these rows are committed to Vuex as they are
// (`courses/setTags`), and a non-configurable own property on an object behind
// a reactive proxy is a needless invariant to defend for no gain here.
const markFromEnvelope = row =>
  Object.defineProperty(row, FROM_ENVELOPE, { value: true, enumerable: false, configurable: true });

/**
 * Whether this row's content came out of an envelope, as opposed to having been
 * stored in the clear. False for a row the API sent as plaintext, and false for
 * anything that never went through the read path.
 *
 * @param {unknown} row
 * @returns {boolean}
 */
export function cameFromEnvelope(row) {
  return row?.[FROM_ENVELOPE] === true;
}

// What an envelope error means to the member. Anything not named here is a row
// that did not open: tampered, corrupt, or not an envelope at all.
const REASON_BY_ERROR = Object.freeze({
  [ENVELOPE_ERRORS.UNKNOWN_VERSION]: UNREADABLE_REASON.NEEDS_NEWER_APP,
  [ENVELOPE_ERRORS.UNKNOWN_KEY]: UNREADABLE_REASON.NO_KEY,
});

// A version this build has never seen is an update waiting to happen; a version
// that is not a version at all is a broken row.
function versionReason(payload) {
  if (payload.v === ENVELOPE_VERSION) return null;
  return Number.isInteger(payload.v) && payload.v > ENVELOPE_VERSION
    ? UNREADABLE_REASON.NEEDS_NEWER_APP
    : UNREADABLE_REASON.FAILED;
}

// Enough to find the row and the key generation it was sealed under; never the
// plaintext, the envelope body or the key.
function warnUnopened(context, payload, error) {
  console.warn(
    `[Annotations] could not open ${context.type} ${context.itemId} (envelope v${payload?.v}, kid ${payload?.kid}): ${error?.code || error?.name || error}`,
  );
}

/**
 * The app's `keyMaterial`: wait for the courses-keys store to finish hydrating,
 * then read what it holds. Rejects with the store's own error (HYDRATION_FAILED,
 * or TIMEOUT after `timeoutMs`), which the opener reports as a failed row.
 */
/**
 * The generation a RUNNING rotation is moving rows to, or null (#1694).
 *
 * Read separately from the key material because it is not about what this
 * device holds — it is about what the SERVER says is happening. A row on this
 * generation that this device cannot open is not a fault; it is the walk, on
 * the member's other phone, and it opens by itself when the rotation ends.
 */
export function rotationTargetFromStore(store) {
  return () => {
    const pendingKeyId = store.getters?.['courses-devices/pendingKeyId'] ?? null;
    if (!pendingKeyId) return null;
    // The generation being emptied is the one to name while the rotation is
    // being UNWOUND (api#1148) — rows move the other way there.
    const target = store.getters?.['courses-devices/rotationAborting']
      ? (store.getters?.['courses-devices/keyId'] ?? null)
      : pendingKeyId;
    return { keyId: target, deviceName: rotatingDeviceName(store) };
  };
}

/** The label of the device running the rotation, or null when nothing names it. */
function rotatingDeviceName(store) {
  const id = store.getters?.['courses-devices/rotation']?.deviceId;
  if (!id) return null;
  const device = (store.getters?.['courses-devices/devices'] ?? []).find(one => one?.id === id);
  // `name` is what the Devices screen shows. Null rather than the raw id: a
  // UUID in a sentence is worse than no device at all.
  return device?.name || null;
}

export function keyMaterialFromStore(store, { timeoutMs = KEY_STORE_READY_TIMEOUT_MS } = {}) {
  return async () => {
    await store.dispatch('courses-keys/ready', { timeoutMs });
    // EVERY generation this device holds, not just the committed one (#1694).
    // While a rotation runs the member's rows are split across two, and a
    // reader that knew only one would show half their notes as unreadable in
    // the middle of an operation that has destroyed nothing.
    const generations = store.getters['courses-keys/readGenerations'];
    if (Array.isArray(generations)) return generations;
    // A store without the rotation getters (an older build, or a spec that
    // stubs only `keyMaterial`) still answers with its one generation.
    const single = store.getters['courses-keys/keyMaterial'];
    return single ? [single] : [];
  };
}

/**
 * The generation an envelope was written under, out of the ones this device
 * holds — or null.
 *
 * SELECTED BY `kid`, NOT TRIED IN TURN. The envelope names its generation, so
 * there is nothing to guess: a row on a generation this device does not hold
 * is reported as NO_KEY rather than as a decryption that failed, and the two
 * stay distinguishable in the log and on the screen.
 *
 * @param {unknown} generations
 * @param {unknown} payload
 * @returns {{ mk: Uint8Array, keyId: string } | null}
 */
function generationFor(generations, payload) {
  const held = Array.isArray(generations) ? generations : [generations];
  const usable = held.filter(entry => entry?.mk && entry?.keyId);
  if (!usable.length) return null;
  const kid = payload?.kid;
  // An envelope with no usable `kid` is malformed rather than unopenable, and
  // `open` says so precisely; hand it the committed generation so the message
  // comes from the one place that can name what is wrong with it.
  if (typeof kid !== 'string') return usable[0];
  return usable.find(entry => entry.keyId === kid) ?? null;
}

/**
 * Build the response hook. `open` defaults to the real envelope primitive;
 * `keyMaterial` must be supplied by whoever installs the hook.
 */
export function createAnnotationOpener({
  open = openEnvelope,
  keyMaterial = () => null,
  rotationTarget = () => null,
} = {}) {
  return async function openAnnotation(context, row) {
    // Before anything else, and for a plaintext row as much as a sealed one:
    // this hook is the single point every annotation row passes through on its
    // way in — a list, a single fetch, or the echo of a write — which makes it
    // the one place that can record what the server says each row's revision
    // is (#1584). A plaintext row carries one too: the column exists for every
    // member, and this device may enrol between reading the row and writing it.
    recordRevision(context?.type, context?.itemId, row?.revision);
    if (!isSealed(row)) return row;
    const { payload, ...plain } = row;
    // Both exits are marked: a row this device could not open was still sealed,
    // and the migration must leave it alone rather than re-write it from the
    // fields it can see.
    const unreadable = reason => markFromEnvelope({ ...plain, unreadable: reason });
    if (!isPlainObject(payload)) return unreadable(UNREADABLE_REASON.FAILED);

    const byVersion = versionReason(payload);
    if (byVersion) return unreadable(byVersion);

    let generations;
    try {
      generations = await keyMaterial();
    } catch (error) {
      warnUnopened(context, payload, error);
      return unreadable(UNREADABLE_REASON.FAILED);
    }
    const keys = generationFor(generations, payload);
    if (!keys?.mk) {
      // ⚠️ THE EXIT THAT USED TO LIE. A row on the generation a running
      // rotation is moving to is not "approve this device" — this device is
      // approved, and no action of the member's would open it. It is the walk,
      // on their other phone, and it opens by itself when the rotation ends.
      //
      // Without this the member watches their notes turn into "Approve this
      // device to read this note", one at a time, as the walk passes over them.
      const rotating = rotationTarget();
      if (rotating?.keyId && payload?.kid === rotating.keyId) {
        return markFromEnvelope({
          ...plain,
          unreadable: UNREADABLE_REASON.BEING_REENCRYPTED,
          // Carried on the row so a screen can name the device without reaching
          // back into the store for a fact this hook has already resolved.
          reencryptingOn: rotating.deviceName ?? null,
        });
      }
      return unreadable(UNREADABLE_REASON.NO_KEY);
    }

    try {
      const fields = await open({
        envelope: payload,
        memberId: context.memberId,
        type: context.type,
        itemId: context.itemId,
        mk: keys.mk,
        keyId: keys.keyId,
      });
      // A shell the sign-out wrote in place of a row it could not open: what
      // it carries is the fact, not fields, and the fact is a reason.
      if (fields?.[UNRECOVERABLE_FIELD] === true) {
        return unreadable(UNREADABLE_REASON.UNRECOVERABLE);
      }
      return markFromEnvelope({ ...plain, ...fields });
    } catch (error) {
      const reason = REASON_BY_ERROR[error?.code] || UNREADABLE_REASON.FAILED;
      if (reason === UNREADABLE_REASON.FAILED) warnUnopened(context, payload, error);
      return unreadable(reason);
    }
  };
}

/** Attach the read path to the transport's response side. */
export function installAnnotationReadPath(deps = {}) {
  setAnnotationTransportHooks({ response: createAnnotationOpener(deps) });
}
